import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useConfig } from "../ConfigProvider";
import { CollapseContextProvider } from "./context/collapseContext";
import classNames from "classnames";
import { motion } from "framer-motion";
import MenuContext from "./context/menuContext";
import { HiChevronDown } from "react-icons/hi";

const MenuCollapse = (props) => {
  const { children, className, eventKey, expanded, label, onToggle } = props;

  const { menuItemHeight, variant, sideCollapsed, defaultExpandedKeys } =
    useContext(MenuContext);

  const { direction } = useConfig();

  // Use expanded prop if provided, otherwise use defaultExpandedKeys or internal state
  const [internalExpanded, setInternalExpanded] = useState(
    expanded !== undefined ? expanded : defaultExpandedKeys.includes(eventKey)
  );

  // Sync with expanded prop from parent (for controlled mode)
  useEffect(() => {
    if (expanded !== undefined) {
      setInternalExpanded(expanded);
    } else if (defaultExpandedKeys.includes(eventKey)) {
      setInternalExpanded(true);
    } else {
      // If not in defaultExpandedKeys and expanded is undefined, close it
      setInternalExpanded(false);
    }
  }, [expanded, eventKey, defaultExpandedKeys]);

  // Use expanded prop if provided (controlled), otherwise use internal state (uncontrolled)
  const isExpanded = expanded !== undefined ? expanded : internalExpanded;

  const toggleCollapse = (e) => {
    const newExpandedState = !isExpanded;
    
    // If controlled (expanded prop provided), call onToggle callback
    // Parent will handle state update
    if (expanded !== undefined && typeof onToggle === "function") {
      onToggle(newExpandedState, e);
    } else {
      // If uncontrolled, update internal state
      setInternalExpanded(newExpandedState);
      if (typeof onToggle === "function") {
        onToggle(newExpandedState, e);
      }
    }
  };

  const getChildrenHeight = () => {
    let height = 0;
    if (isExpanded && children && children.length) {
      height = children.length * menuItemHeight;
    }
    if (isExpanded && children && !children.length) {
      height = menuItemHeight;
    }
    return height;
  };

  const menuCollapseItemClass = classNames(
    "menu-collapse-item ",
    `menu-collapse-item-${variant}`,
    className
  );

  return (
    <div className="menu-collapse ">
      <div className={menuCollapseItemClass} onClick={toggleCollapse}>
        <span className="flex items-center text-black md:text-inherit">
          {label}
        </span>
        <motion.span
          className="text-lg mt-1 text-black md:text-inherit"
          initial={{ transform: "rotate(0deg) " }}
          animate={{
            transform: isExpanded ? "rotate(-180deg)" : "rotate(0deg)",
          }}
          transition={{ duration: 0.15 }}
        >
          {sideCollapsed ? null : <HiChevronDown />}
        </motion.span>
      </div>
      <CollapseContextProvider value={isExpanded}>
        <motion.ul
          className={direction === "rtl" ? "mr-5" : "ml-4"}
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: isExpanded ? 1 : 0,
            height: isExpanded ? getChildrenHeight() : 0,
          }}
          transition={{ duration: 0.15 }}
          style={{
            overflow: isExpanded ? "visible" : "hidden",
            visibility: isExpanded ? "visible" : "hidden",
          }}
        >
          {children}
        </motion.ul>
      </CollapseContextProvider>
    </div>
  );
};

MenuCollapse.propTypes = {
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  className: PropTypes.string,
};

MenuCollapse.defaultProps = {
  expanded: false,
  label: null,
};

export default MenuCollapse;